import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  User, 
  Target, 
  Building,
  Image as ImageIcon,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../../components/SEO';

const CreatePetition = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    target_person: '',
    target_designation: '',
    target_organization: '',
    grievance_details: '',
    about_campaigner: '',
    photo_url: '',
    evidence_urls: []
  });
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;
  const totalSteps = 5;

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('causes_token');
    const userData = localStorage.getItem('causes_user');
    
    if (!token || !userData) {
      toast.error('Please login to create a petition');
      navigate('/causes/login');
      return;
    }

    setUser(JSON.parse(userData));
  }, [navigate]);

  const steps = [
    { 
      number: 1, 
      title: 'Petition Title', 
      description: 'What do you want to change?',
      icon: FileText 
    },
    { 
      number: 2, 
      title: 'Target Person', 
      description: 'Who should address this?',
      icon: Target 
    },
    { 
      number: 3, 
      title: 'Your Grievance', 
      description: 'Explain the problem in detail',
      icon: AlertCircle 
    },
    { 
      number: 4, 
      title: 'About You', 
      description: 'Why is this important to you?',
      icon: User 
    },
    { 
      number: 5, 
      title: 'Add Photos', 
      description: 'Supporting images (optional)',
      icon: ImageIcon 
    }
  ];

  const validateStep = () => {
    setError('');
    
    switch(currentStep) {
      case 1:
        if (!formData.title.trim()) {
          setError('Please enter a title for your petition');
          return false;
        }
        if (formData.title.length < 10) {
          setError('Title should be at least 10 characters');
          return false;
        }
        break;
      case 2:
        // Optional fields, always valid
        break;
      case 3:
        if (!formData.grievance_details.trim()) {
          setError('Please describe your grievance');
          return false;
        }
        if (formData.grievance_details.length < 50) {
          setError('Please provide more details (at least 50 characters)');
          return false;
        }
        break;
      case 4:
        if (!formData.about_campaigner.trim()) {
          setError('Please tell us about yourself');
          return false;
        }
        break;
      case 5:
        // Optional fields
        break;
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    const token = localStorage.getItem('causes_token');

    try {
      const response = await axios.post(
        `${API}/api/petitions/create`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { campaigner_id: user.id }
        }
      );

      toast.success('Petition created successfully!');
      navigate(`/causes/dashboard`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create petition. Please try again.');
      toast.error('Failed to create petition');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="create-petition-page">
      <SEO title="Start a Petition - Elderra Causes" description="Create your petition" />

      <div className="create-petition-container">
        {/* Progress Bar */}
        <div className="petition-progress-bar">
          <div className="progress-steps">
            {steps.map((step) => (
              <div 
                key={step.number}
                className={`progress-step ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}
              >
                <div className="progress-step-number">{step.number}</div>
                <div className="progress-step-label">{step.title}</div>
              </div>
            ))}
          </div>
          <div className="progress-bar-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }}></div>
        </div>

        {/* Form Card */}
        <div className="petition-form-card">
          <div className="petition-form-header">
            <div className="step-icon">
              {React.createElement(steps[currentStep - 1].icon, { size: 48, color: "#e5266a" })}
            </div>
            <h1 className="petition-step-title">{steps[currentStep - 1].title}</h1>
            <p className="petition-step-description">{steps[currentStep - 1].description}</p>
          </div>

          {error && (
            <div className="petition-error-alert">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Title */}
          {currentStep === 1 && (
            <div className="petition-form-content">
              <div className="form-group-large">
                <label className="label-large">What change do you want to see?</label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Example: Better healthcare facilities for seniors in Delhi"
                  className="input-large"
                  maxLength={150}
                />
                <p className="form-hint-large">
                  Write a clear, specific title ({formData.title.length}/150 characters)
                </p>
              </div>

              <div className="form-tips-box">
                <h4>💡 Tips for a good title:</h4>
                <ul>
                  <li>Be specific about what you want</li>
                  <li>Mention the location if relevant</li>
                  <li>Keep it under 150 characters</li>
                  <li>Make it easy to understand</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Target */}
          {currentStep === 2 && (
            <div className="petition-form-content">
              <div className="form-group-large">
                <label className="label-large">Who should address this issue?</label>
                <Input
                  type="text"
                  value={formData.target_person}
                  onChange={(e) => setFormData({ ...formData, target_person: e.target.value })}
                  placeholder="Example: Dr. Ramesh Kumar (Optional)"
                  className="input-large"
                />
                <p className="form-hint-large">
                  Name of the person (CEO, Minister, Officer, etc.)
                </p>
              </div>

              <div className="form-group-large">
                <label className="label-large">Their designation/position</label>
                <Input
                  type="text"
                  value={formData.target_designation}
                  onChange={(e) => setFormData({ ...formData, target_designation: e.target.value })}
                  placeholder="Example: Health Minister (Optional)"
                  className="input-large"
                />
              </div>

              <div className="form-group-large">
                <label className="label-large">Organization/Department</label>
                <Input
                  type="text"
                  value={formData.target_organization}
                  onChange={(e) => setFormData({ ...formData, target_organization: e.target.value })}
                  placeholder="Example: Ministry of Health, Delhi (Optional)"
                  className="input-large"
                />
              </div>

              <div className="info-box-note">
                <p>📝 You can skip this step if you're not sure who should address this</p>
              </div>
            </div>
          )}

          {/* Step 3: Grievance */}
          {currentStep === 3 && (
            <div className="petition-form-content">
              <div className="form-group-large">
                <label className="label-large">Describe your grievance in detail</label>
                <Textarea
                  value={formData.grievance_details}
                  onChange={(e) => setFormData({ ...formData, grievance_details: e.target.value })}
                  placeholder="Explain the problem you're facing, why it matters, and what change you want to see. Be specific with examples."
                  className="textarea-large"
                  rows={10}
                />
                <p className="form-hint-large">
                  Provide details ({formData.grievance_details.length} characters)
                </p>
              </div>

              <div className="form-tips-box">
                <h4>💡 What to include:</h4>
                <ul>
                  <li>What is the problem?</li>
                  <li>How does it affect you and others?</li>
                  <li>What solution do you want?</li>
                  <li>Why is this important?</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 4: About You */}
          {currentStep === 4 && (
            <div className="petition-form-content">
              <div className="form-group-large">
                <label className="label-large">Tell us about yourself</label>
                <Textarea
                  value={formData.about_campaigner}
                  onChange={(e) => setFormData({ ...formData, about_campaigner: e.target.value })}
                  placeholder="Write a short paragraph about who you are and why this issue is important to you personally."
                  className="textarea-large"
                  rows={8}
                />
                <p className="form-hint-large">
                  This helps supporters understand your story
                </p>
              </div>

              <div className="example-box">
                <h4>Example:</h4>
                <p>
                  "I am a 68-year-old retired teacher from Mumbai. I face difficulties accessing 
                  healthcare services in my area and believe senior citizens deserve better 
                  facilities. I'm starting this petition to make a difference."
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Photos */}
          {currentStep === 5 && (
            <div className="petition-form-content">
              <div className="form-group-large">
                <label className="label-large">Add a photo (Optional)</label>
                <Input
                  type="url"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  placeholder="Paste image URL here"
                  className="input-large"
                />
                <p className="form-hint-large">
                  A relevant image helps your petition stand out
                </p>
              </div>

              <div className="info-box-note">
                <p>📷 You can skip this step and add images later from your dashboard</p>
              </div>

              <div className="petition-summary-box">
                <h3>Review Your Petition:</h3>
                <div className="summary-item">
                  <strong>Title:</strong> {formData.title || 'Not set'}
                </div>
                {formData.target_person && (
                  <div className="summary-item">
                    <strong>Target:</strong> {formData.target_person}
                  </div>
                )}
                <div className="summary-item">
                  <strong>Grievance:</strong> {formData.grievance_details.substring(0, 100)}...
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="petition-form-actions">
            {currentStep > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="btn-large-secondary"
                disabled={loading}
              >
                <ArrowLeft size={24} />
                Back
              </Button>
            )}

            {currentStep < totalSteps ? (
              <Button
                onClick={handleNext}
                className="btn-large-primary"
              >
                Next Step
                <ArrowRight size={24} />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                className="btn-large-primary"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Petition'}
                <CheckCircle size={24} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePetition;
